import { type NotificaResponseDTO } from '@framework/api-client/api/backoffice/schemas/notificaResponseDTO.ts';
import { type NotificaUtenteResponseDTO } from '@framework/api-client/api/inquiries-compile/schemas/notificaUtenteResponseDTO.ts';
import { FilterBar } from '@framework/ui/components/search/filter-bar/FilterBar.tsx';
import { type SearchableField } from '@framework/ui/components/search/ISearchableField.tsx';
import { TableData } from '@framework/ui/components/table/TableData.tsx';
import { usePaginationUrlQueryState } from '@framework/ui/core/pagination/paginationUrlQuery.ts';
import { useHistory } from '@framework/ui/core/router/TypedRoute.tsx';
import { type yup } from '@framework/validation/initYup.ts';
import { type ColumnsType } from 'antd/es/table';
import { useCallback } from 'react';
import styled from 'styled-components';
import { useNotification } from '../hook/useNotification.ts';
import { type NotificationListParams } from '../search/NotificationListParams.ts';
import {
  useNotificationPaginationChange,
  useNotificationSortedColumns,
} from './useNotificationPaginationChange.ts';

export type { NotificationListParams } from '../search/NotificationListParams.ts';

const PAGINATION_IGNORED_KEYS: Array<keyof NotificationListParams> = [
  'sortField',
  'sortDirection',
  'firstResult',
  'maxResults',
];

const NotificationTableWrapper = styled.div`
  /* Oggetto sempre in grassetto */
  .ant-table-tbody .col-oggetto {
    font-weight: 600;
  }
  /* Righe lette: opacità ridotta */
  .notification-row-read {
    opacity: 0.65;
  }
  /* Righe non lette: Messaggio in semi-bold */
  .notification-row-unread .col-messaggio {
    font-weight: 600;
  }
`;

export interface INotificationTableDataProps {
  columns: ColumnsType<NotificaResponseDTO | NotificaUtenteResponseDTO>;
  searchSchema: yup.ObjectSchema<NotificationListParams>;
  searchableFields: SearchableField[];
  isBackoffice?: boolean;
}

const DEFAULT_PAGE_SIZE = 20;

const DEFAULT_QUERY: NotificationListParams = {
  firstResult: 0,
  maxResults: DEFAULT_PAGE_SIZE,
  // Valori di default indipendenti da enum specifici di un singolo client API
  sortField: 'DATA_NOTIFICA' as NotificationListParams['sortField'],
  sortDirection: 'DESC' as NotificationListParams['sortDirection'],
};

/**
 * Componente che rappresenta la tabella delle notifiche, utilizzata all'interno della pagina delle notifiche.
 */
export function NotificationTableData(props: INotificationTableDataProps) {
  const {
    columns: notificationColumns,
    searchSchema,
    searchableFields,
    isBackoffice = false,
    ...otherProps
  } = props;
  const history = useHistory();

  const [query, setQuery] = usePaginationUrlQueryState<NotificationListParams>({
    history,
    input: DEFAULT_QUERY,
  });

  const { apiResult, notifications, meta } = useNotification(query);

  const columns = useNotificationSortedColumns(notificationColumns, query);
  const onPaginationChange = useNotificationPaginationChange(query, setQuery);

  const pageSize = Number(query.maxResults ?? DEFAULT_PAGE_SIZE);

  const filterBar = (
    <FilterBar<NotificationListParams>
      id="notification-list-search"
      fields={searchableFields}
      schema={searchSchema}
      pageKey="firstResult"
      query={query}
      defaultQuery={DEFAULT_QUERY}
      setQuery={setQuery}
      total={meta.total ?? 0}
      ignoredKeys={PAGINATION_IGNORED_KEYS}
      showRefresh
      layout={{ showInCard: true }}
    />
  );

  const tableTitle = useCallback(() => {
    return <div>{filterBar}</div>;
  }, [filterBar]);

  return (
    <NotificationTableWrapper>
      <TableData
        {...otherProps}
        title={tableTitle}
        size={isBackoffice ? undefined : 'small'}
        uppercaseThead={isBackoffice}
        columns={columns}
        darkTitleBar={false}
        showSorterTooltip={false}
        loading={apiResult.isLoading || apiResult.isFetching}
        dataSource={notifications ?? []}
        rowClassName={record => {
          const r = record as NotificaResponseDTO;
          return r.dataPresaVisione
            ? 'notification-row-read'
            : 'notification-row-unread';
        }}
        onChange={onPaginationChange}
        pagination={{
          defaultPageSize: DEFAULT_PAGE_SIZE,
          current: Math.floor(Number(query.firstResult ?? 0) / pageSize) + 1,
          pageSize,
          total: meta.total ?? 0,
        }}
      />
    </NotificationTableWrapper>
  );
}
